import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { Clock, MapPin, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSettings } from '../../contexts/SettingsContext';
import { useDarkMode } from '../../contexts/DarkModeContext';
import { getCountdown, adjustPrayerTimes, haveAllPrayersPassed } from '../../utils/prayerTimeUtils';
import { useLocation } from '../../contexts/LocationContext';

// Create a memoized sub-component for prayer time entries
const PrayerTimeEntry = React.memo(({ prayer, time, isNext, isDarkMode }) => {
  return (
    <div 
      key={prayer} 
      className={`group relative flex items-center justify-between px-4 py-2 rounded-xl transition-all duration-300 ${
        isNext 
          ? isDarkMode 
            ? 'bg-gradient-to-r from-emerald-950/40 to-emerald-900/30 border border-emerald-500/20'
            : 'bg-gradient-to-r from-emerald-50/80 to-white/60 border border-emerald-100/30'
          : ''
      }`}
    >
      {/* Add subtle highlight effect for the next prayer */}
      {isNext && (
        <div className={`absolute inset-0 rounded-xl ${
          isDarkMode 
            ? 'bg-gradient-to-r from-emerald-500/5 to-transparent'
            : 'bg-gradient-to-r from-emerald-200/30 to-transparent'
        }`}></div>
      )}
      
      <div className="flex items-center gap-4 relative z-10 text-rendering-optimizeLegibility">
        <div className="flex items-center gap-3">
          <span className={`text-sm font-medium antialiased ${
            isNext 
              ? isDarkMode ? 'text-emerald-300' : 'text-emerald-700'
              : isDarkMode ? 'text-gray-500' : 'text-gray-500'
          }`}>
            {prayer}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-4 relative z-10 text-rendering-optimizeLegibility">
        <span className={`text-base font-medium antialiased ${
          isNext 
            ? isDarkMode ? 'text-emerald-300 font-semibold' : 'text-emerald-700 font-semibold'
            : isDarkMode ? 'text-gray-300' : 'text-gray-600'
        }`}>
          {time}
        </span>
        <div className={`rounded-full ${
          isNext 
            ? isDarkMode 
              ? 'w-2.5 h-2.5 bg-emerald-500 animate-pulse shadow-sm shadow-emerald-500/30' 
              : 'w-2.5 h-2.5 bg-emerald-500 animate-pulse shadow-sm shadow-emerald-500/30'
            : isDarkMode ? 'w-2 h-2 bg-gray-700' : 'w-2 h-2 bg-gray-200'
        }`} />
      </div>
    </div>
  );
});

const PrayerTimesCard = ({ prayerTimes, tomorrowPrayerTimes, error, hideTitle, isLoading }) => {
  const navigate = useNavigate();
  const { settings, updateSettings, isInitialized } = useSettings();
  const { isDarkMode } = useDarkMode();
  const { getLocation } = useLocation();
  const [showTomorrow, setShowTomorrow] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isSettingLocation, setIsSettingLocation] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [showLocationCTA, setShowLocationCTA] = useState(false);
  const ctaTimeoutRef = useRef();
  
  // Use a ref to track the previous prayer times to avoid unnecessary re-renders
  const prevPrayerTimesRef = useRef(null);

  // Debug the rendering conditions
  useEffect(() => {
    // Compare with previous times to see if we're actually getting new data
    const prevTimes = prevPrayerTimesRef.current ? 
      JSON.stringify(prevPrayerTimesRef.current) : 'none';
    const newTimes = prayerTimes?.times ? 
      JSON.stringify(prayerTimes.times) : 'none';
    const hasChanged = prevTimes !== newTimes;
    
    if (hasChanged) {
      console.log('[PrayerTimesCard] Rendering with props:', { 
        isLoading, 
        hasCoordinates: !!settings?.coordinates, 
        hasLocation: !!settings?.location,
        hasPrayerTimes: !!prayerTimes,
        prayerTimesHasTimes: !!prayerTimes?.times,
        prayerTimesKeys: prayerTimes?.times ? Object.keys(prayerTimes.times) : []
      });
      
      // Update the ref
      prevPrayerTimesRef.current = prayerTimes?.times;
    }
  }, [isLoading, settings, prayerTimes]);

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  // Handle automatic location setting
  const handleSetLocation = useCallback(async () => {
    setIsSettingLocation(true);
    setLocationError(null);

    try {
      // Use the getLocation method from LocationContext instead of navigator.geolocation directly
      const coords = await getLocation();
      
      if (coords) {
        const { latitude, longitude } = coords;

        // Get place name using reverse geocoding
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`
          );
          const data = await response.json();
          
          // Extract place name using address components - similar to the OnboardingCarousel approach
          const placeName = data.address?.city || 
                           data.address?.town || 
                           data.address?.village || 
                           data.address?.suburb ||
                           (data.display_name ? data.display_name.split(',')[0].trim() : 'Unknown Location');
          
          // Update settings with the correct coordinate format (lon not lng)
          await updateSettings({
            ...settings,
            coordinates: { 
              lat: latitude, 
              lon: longitude  // Use 'lon' instead of 'lng' for consistency
            },
            location: placeName
          });

          setLocationError(null);
        } catch (error) {
          console.error('[PrayerTimesCard] Error fetching location name:', error);
          setLocationError('Failed to fetch location name. Please try again.');
          
          // Still save coordinates even if we couldn't get the location name
          await updateSettings({
            ...settings,
            coordinates: { 
              lat: latitude, 
              lon: longitude
            },
            location: 'Unknown Location'
          });
        }
      } else {
        throw new Error("Location coordinates not received");
      }
    } catch (error) {
      console.error('[PrayerTimesCard] Error detecting location:', error);
      
      // More descriptive error message for native app users
      if (window.isUmmahtiNative) {
        setLocationError(
          "Unable to access your location. Please ensure location permissions are granted in your device settings, then try again."
        );
      } else {
        setLocationError(
          error.code === 1 
            ? 'Please enable location access in your browser settings.'
            : error.message || 'Could not get your location. Please try again.'
        );
      }
    } finally {
      setIsSettingLocation(false);
    }
  }, [getLocation, settings, updateSettings]);

  // Check if all prayers have passed and we should show tomorrow's times
  useEffect(() => {
    if (prayerTimes && prayerTimes.times && Object.keys(prayerTimes.times).length > 0) {
      const allPrayersPassed = haveAllPrayersPassed(prayerTimes.times, currentTime, settings?.tunedTimes || {});
      setShowTomorrow(allPrayersPassed && tomorrowPrayerTimes && tomorrowPrayerTimes.times && 
                     Object.keys(tomorrowPrayerTimes.times).length > 0);
    }
  }, [prayerTimes, tomorrowPrayerTimes, currentTime, settings?.tunedTimes]);

  // Determine which prayer times to display
  const displayPrayerTimes = useMemo(() => {
    return showTomorrow ? tomorrowPrayerTimes : prayerTimes;
  }, [showTomorrow, tomorrowPrayerTimes, prayerTimes]);

  // Apply tuned prayer times only if they haven't been adjusted yet
  const adjustedPrayerTimes = useMemo(() => {
    // Return empty object if prerequisites are not met
    if (!settings || !settings.coordinates || !settings.location) {
      return {};
    }
    
    if (!displayPrayerTimes?.times || Object.keys(displayPrayerTimes.times).length === 0) {
      return {};
    }

    // Cache result to avoid unnecessary calculations
    // Check if the times are already stored in a ref so we don't recalculate unnecessarily
    if (displayPrayerTimes.isAdjusted) {
      return displayPrayerTimes.times;
    }

    // Apply local adjustments for consistency across components
    try {
      const adjusted = Object.entries(displayPrayerTimes.times).reduce((acc, [prayer, time]) => {
        if (!time) {
          acc[prayer] = '';
          return acc;
        }
        
        const adjustment = settings?.tunedTimes?.[prayer] || 0;
        
        if (adjustment === 0) {
          acc[prayer] = time;
          return acc;
        }

        const [hours, minutes] = time.split(':').map(num => parseInt(num, 10));
        if (isNaN(hours) || isNaN(minutes)) {
          acc[prayer] = time; // Keep original if parsing fails
          return acc;
        }
        
        let totalMinutes = (hours * 60 + minutes + adjustment) % (24 * 60);
        if (totalMinutes < 0) totalMinutes += 24 * 60;
        
        const adjustedHours = Math.floor(totalMinutes / 60);
        const adjustedMinutes = totalMinutes % 60;
        
        acc[prayer] = `${adjustedHours.toString().padStart(2, '0')}:${adjustedMinutes.toString().padStart(2, '0')}`;
        return acc;
      }, {});

      return adjusted;
    } catch (err) {
      console.error('[PrayerTimesCard] Error adjusting prayer times:', err);
      return displayPrayerTimes.times || {};
    }
  }, [displayPrayerTimes, settings]);

  // Delay before showing the Set Location CTA to avoid flicker
  useEffect(() => {
    // Only start timer if settings are initialized and location/coordinates are missing
    if (isInitialized && settings && (!settings.coordinates || !settings.location)) {
      setShowLocationCTA(false);
      ctaTimeoutRef.current = setTimeout(() => {
        // Only show CTA if still missing after delay
        if (settings && (!settings.coordinates || !settings.location)) {
          setShowLocationCTA(true);
        }
      }, 400);
    } else {
      // If location/coordinates become available, hide CTA immediately
      setShowLocationCTA(false);
      if (ctaTimeoutRef.current) {
        clearTimeout(ctaTimeoutRef.current);
      }
    }
    return () => {
      if (ctaTimeoutRef.current) {
        clearTimeout(ctaTimeoutRef.current);
      }
    };
  }, [isInitialized, settings]);

  // Get the next prayer using adjusted times - memoized to avoid recalculation
  const nextPrayer = useMemo(() => {
    if (!adjustedPrayerTimes || Object.keys(adjustedPrayerTimes).length === 0) return null;
    const { nextPrayer } = getCountdown(adjustedPrayerTimes, currentTime);
    return nextPrayer;
  }, [adjustedPrayerTimes, currentTime]);

  // Show loading skeleton if settings are not initialized
  if (!isInitialized || isLoading) {
    console.log('[PrayerTimesCard] Rendering loading state');
    return (
      <div className="flex flex-col h-full">
        <div className={`flex items-center ${hideTitle ? 'hidden' : 'gap-2 mb-4'}`}>
          <Clock className={isDarkMode ? "w-5 h-5 text-emerald-400" : "w-5 h-5 text-emerald-600"} />
          <h2 className={`text-lg font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}>
            Prayer Times
          </h2>
        </div>
        
        <div className="flex-1 flex flex-col justify-between">
          <div className="flex h-full flex-col justify-between animate-pulse">
            {[...Array(5)].map((_, index) => (
              <div 
                key={index}
                className={`flex items-center justify-between px-4 py-2 rounded-xl`}
              >
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-5 bg-gray-200 dark:bg-gray-700 rounded" />
                    <div className="w-20 h-5 bg-gray-200 dark:bg-gray-700 rounded" />
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-14 h-5 bg-gray-200 dark:bg-gray-700 rounded" />
                  <div className="w-2 h-2 rounded-full bg-gray-200 dark:bg-gray-700" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Then check for coordinates or location, with delay to avoid flicker
  if (isInitialized && (!settings.coordinates || !settings.location) && showLocationCTA) {
    console.log('[PrayerTimesCard] Rendering Set Location CTA', {
      hasCoordinates: !!settings.coordinates,
      hasLocation: !!settings.location,
      isLoading,
      hasPrayerTimes: !!prayerTimes,
      hasPrayerTimesTimes: !!prayerTimes?.times
    });
    return (
      <div className="h-full flex flex-col">
        <div className={`flex items-center justify-between ${hideTitle ? 'hidden' : 'mb-4'}`}>
          <div className="flex items-center gap-2">
            <Clock className={isDarkMode ? "w-5 h-5 text-emerald-400" : "w-5 h-5 text-emerald-600"} />
            <h2 className={`text-lg font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}>
              Prayer Times
            </h2>
          </div>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
          <img src="/images/empty_states/location_empty_state.png" alt="Set location" className="w-70 h-70 mx-auto mb-6" />
          <h3 className={`text-xl font-medium mb-3 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            Set Your Location
          </h3>
          <p className={`text-sm max-w-xs mx-auto mb-6 ${
            isDarkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Enable location access to get accurate prayer times for your area.
          </p>
          {locationError && (
            <p className="text-sm text-red-500 mb-4">{locationError}</p>
          )}
          <button
            onClick={handleSetLocation}
            disabled={isSettingLocation}
            className={`px-4 py-2 rounded-xl transition-all duration-200 ${
              isDarkMode
                ? 'bg-emerald-600/20 text-emerald-400 hover:bg-emerald-600/30'
                : 'bg-emerald-600 text-white hover:bg-emerald-700'
            } ${isSettingLocation ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isSettingLocation ? (
              <div className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Detecting location...</span>
              </div>
            ) : 'Use My Location'}
          </button>
        </div>
      </div>
    );
  }

  // Then check for empty prayer times
  if (!displayPrayerTimes?.times || Object.keys(adjustedPrayerTimes).length === 0) {
    return (
      <div className="text-center py-4">
        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          No prayer times available.
        </p>
      </div>
    );
  }

  // Finally check for errors
  if (error) {
    console.log('[PrayerTimesCard] Rendering error message. settings:', { location: settings.location, coordinates: settings.coordinates, full: settings });
    return (
      <div className="text-center py-4">
        <p className={`text-sm ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
          {error}
        </p>
      </div>
    );
  }

  // Add this helper function to extract city name
  const getDisplayLocation = (location) => {
    if (!location) return '';
    // Split by commas and take first part (usually the city)
    return location.split(',')[0].trim();
  };

  return (
    <div className="flex flex-col h-full">
      <div className={`flex items-center justify-between ${hideTitle ? 'hidden' : 'mb-4'}`}>
        <div className="flex items-center gap-2">
          <Clock className={isDarkMode ? "w-5 h-5 text-emerald-400" : "w-5 h-5 text-emerald-600"} />
          <h2 className={`text-lg font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}>
            Prayer Times
          </h2>
        </div>
        
        <div className="flex items-center gap-2">
          {settings?.location && (
            <div className="flex items-center gap-1">
              <MapPin className={`w-3.5 h-3.5 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`} />
              <span className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                {getDisplayLocation(settings.location)}
              </span>
            </div>
          )}
          {showTomorrow && (
            <div className="flex items-center gap-1">
              <Calendar className={`w-4 h-4 ${isDarkMode ? "text-emerald-400" : "text-emerald-600"}`} />
              <span className={`text-sm ${isDarkMode ? "text-emerald-400" : "text-emerald-600"}`}>
                Tomorrow
              </span>
            </div>
          )}
        </div>
      </div>
      
      <div className="flex-1 flex flex-col justify-between">
        <div className="flex h-full flex-col justify-between">
          {Object.entries(adjustedPrayerTimes).map(([prayer, time]) => {
            const isNext = prayer === nextPrayer;
            return (
              <PrayerTimeEntry
                key={prayer}
                prayer={prayer}
                time={time}
                isNext={isNext}
                isDarkMode={isDarkMode}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default React.memo(PrayerTimesCard);